import { useEffect, useRef, useState } from 'react'
import { Input } from '@/shared/components/atoms/ui/input'
import { peopleApi, type EntraSearchResult } from '@/features/people'

type SearchResult = EntraSearchResult

interface EmailAutocompleteProps {
	value: string
	onChange: (email: string) => void
	placeholder?: string
	className?: string
}

export function EmailAutocomplete({
	value,
	onChange,
	placeholder = 'Search by name or email...',
	className,
}: EmailAutocompleteProps) {
	const [query, setQuery] = useState(value)
	const [results, setResults] = useState<SearchResult[]>([])
	const [isOpen, setIsOpen] = useState(false)
	const [isSearching, setIsSearching] = useState(false)
	const containerRef = useRef<HTMLDivElement>(null)
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		setQuery(value)
	}, [value])

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setIsOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	const search = (q: string) => {
		setQuery(q)

		if (q.length < 2) {
			setResults([])
			setIsOpen(false)
			return
		}

		if (debounceRef.current) clearTimeout(debounceRef.current)
		debounceRef.current = setTimeout(() => {
			setIsSearching(true)
			void peopleApi
				.searchEntra(q)
				.then((items) => {
					const withEmail = items.filter((r) => r.email)
					setResults(withEmail)
					setIsOpen(withEmail.length > 0)
				})
				.catch(() => setResults([]))
				.finally(() => setIsSearching(false))
		}, 300)
	}

	const selectResult = (result: SearchResult) => {
		const email = result.email ?? ''
		setQuery(email)
		onChange(email)
		setIsOpen(false)
		setResults([])
	}

	const handleBlur = () => {
		// If user typed a raw email without selecting from dropdown, accept it
		setTimeout(() => {
			if (!isOpen) {
				onChange(query)
			}
		}, 200)
	}

	return (
		<div ref={containerRef} className="relative">
			<Input
				value={query}
				onChange={(e) => search(e.target.value)}
				onFocus={() => results.length > 0 && setIsOpen(true)}
				onBlur={handleBlur}
				placeholder={placeholder}
				className={className}
			/>
			{isSearching && (
				<div className="absolute right-2 top-1/2 -translate-y-1/2">
					<div className="animate-spin rounded-full h-3 w-3 border border-muted-foreground border-t-transparent" />
				</div>
			)}
			{isOpen && results.length > 0 && (
				<div className="absolute z-50 w-full mt-1 bg-white border border-border rounded-md shadow-lg max-h-48 overflow-auto">
					{results.map((r) => (
						<button
							key={r.entraDirectoryId}
							type="button"
							className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex flex-col"
							onMouseDown={(e) => e.preventDefault()}
							onClick={() => selectResult(r)}
						>
							<span className="font-medium text-foreground">{r.displayName}</span>
							{r.email && <span className="text-xs text-muted-foreground">{r.email}</span>}
						</button>
					))}
				</div>
			)}
		</div>
	)
}
